import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { FaAtom, FaCog, FaCoffee, FaCode } from "react-icons/fa";

function ThreeHomeScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = container.clientWidth || 650;
    const height = container.clientHeight || 620;

    // 1. Scene, Camera, Renderer
    const scene = new THREE.Scene();
    scene.position.y = 0.65; // Shift 3D galaxy scene up further
    scene.scale.set(0.85, 0.85, 0.85); // Scale down slightly so it never overflows
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    // Elevated 35-degree isometric camera with comfortable view distance
    camera.position.set(0, 3.4, 7.8);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    container.appendChild(renderer.domElement);

    // 2. Multi-color Cosmic Ambient & Galaxy Lights
    const ambientLight = new THREE.AmbientLight(0x0f172a, 1.6);
    scene.add(ambientLight);

    // Core Supernova Point Light
    const coreLight = new THREE.PointLight(0xa78bfa, 5.0, 16);
    coreLight.position.set(0, 0, 0);
    scene.add(coreLight);

    // Cyan Spiral Light
    const cyanLight = new THREE.DirectionalLight(0x38bdf8, 1.6);
    cyanLight.position.set(5, 6, 4);
    scene.add(cyanLight);

    // Magenta Spiral Light
    const magentaLight = new THREE.DirectionalLight(0xec4899, 1.4);
    magentaLight.position.set(-5, -4, -2);
    scene.add(magentaLight);

    // 3. Central 3D Earth (Trái Đất) Core with Atmosphere & Clouds
    const createEarthTexture = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");

      // Deep Blue Oceans with depth gradient
      const oceanGrad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      oceanGrad.addColorStop(0, "#081d36");
      oceanGrad.addColorStop(0.5, "#0e3863");
      oceanGrad.addColorStop(1, "#081d36");
      ctx.fillStyle = oceanGrad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Continents (Emerald green & forest landmasses)
      ctx.fillStyle = "#10b981";
      const numContinents = 45;
      for (let i = 0; i < numContinents; i++) {
        const cx = Math.random() * canvas.width;
        const cy = 60 + Math.random() * (canvas.height - 120);
        const radius = 35 + Math.random() * 85;

        ctx.beginPath();
        for (let a = 0; a < Math.PI * 2; a += 0.25) {
          const r = radius + (Math.random() - 0.5) * 35;
          const x = cx + Math.cos(a) * r;
          const y = cy + Math.sin(a) * r;
          if (a === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();

        // Inner continent mountain contours
        ctx.fillStyle = "#059669";
        ctx.beginPath();
        ctx.arc(cx, cy, radius * 0.55, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#10b981";
      }

      // North & South Polar Ice Caps
      ctx.fillStyle = "#e2e8f0";
      ctx.beginPath();
      ctx.ellipse(canvas.width / 2, 20, canvas.width / 2, 30, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(canvas.width / 2, canvas.height - 20, canvas.width / 2, 30, 0, 0, Math.PI * 2);
      ctx.fill();

      return new THREE.CanvasTexture(canvas);
    };

    const createCloudTexture = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");

      ctx.fillStyle = "rgba(0,0,0,0)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
      for (let i = 0; i < 70; i++) {
        const cx = Math.random() * canvas.width;
        const cy = Math.random() * canvas.height;
        const radius = 25 + Math.random() * 75;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      return new THREE.CanvasTexture(canvas);
    };

    const coreGroup = new THREE.Group();

    // 3D Earth Surface
    const earthTexture = createEarthTexture();
    const earthGeo = new THREE.SphereGeometry(0.7, 64, 64);
    const earthMat = new THREE.MeshStandardMaterial({
      map: earthTexture,
      roughness: 0.45,
      metalness: 0.15,
    });
    const earthMesh = new THREE.Mesh(earthGeo, earthMat);
    coreGroup.add(earthMesh);

    // Rotating Atmosphere Clouds Layer
    const cloudTexture = createCloudTexture();
    const cloudGeo = new THREE.SphereGeometry(0.72, 64, 64);
    const cloudMat = new THREE.MeshStandardMaterial({
      map: cloudTexture,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
    });
    const cloudMesh = new THREE.Mesh(cloudGeo, cloudMat);
    coreGroup.add(cloudMesh);

    // Glowing Blue Outer Atmosphere Aura (Corona)
    const atmosphereGeo = new THREE.SphereGeometry(0.82, 32, 32);
    const atmosphereMat = new THREE.MeshBasicMaterial({
      color: 0x38bdf8,
      transparent: true,
      opacity: 0.25,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
    });
    const atmosphereMesh = new THREE.Mesh(atmosphereGeo, atmosphereMat);
    coreGroup.add(atmosphereMesh);

    // Core Wireframe Orbital Shield
    const shieldGeo = new THREE.IcosahedronGeometry(0.92, 1);
    const shieldMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8, wireframe: true, transparent: true, opacity: 0.35 });
    const shield = new THREE.Mesh(shieldGeo, shieldMat);
    coreGroup.add(shield);

    scene.add(coreGroup);

    // 4. Spiral Milky Way Galaxy Arms (1,500+ Star Particles)
    const galaxyParticlesGeo = new THREE.BufferGeometry();
    const particleCount = 1500;
    const posArray = new Float32Array(particleCount * 3);
    const colorArray = new Float32Array(particleCount * 3);

    const colors = [
      new THREE.Color(0x38bdf8), // Cyan
      new THREE.Color(0xc084fc), // Purple
      new THREE.Color(0xec4899), // Magenta
      new THREE.Color(0xf59e0b), // Gold
    ];

    for (let i = 0; i < particleCount; i++) {
      // Logarithmic Spiral Arms formula (Scaled down max radius to 3.2)
      const arm = i % 2 === 0 ? 0 : Math.PI;
      const radius = Math.pow(Math.random(), 1.5) * 3.2 + 0.4;
      const spinAngle = radius * 1.6 + arm;
      const randomX = (Math.random() - 0.5) * (radius * 0.22);
      const randomY = (Math.random() - 0.5) * 0.35;
      const randomZ = (Math.random() - 0.5) * (radius * 0.22);

      posArray[i * 3] = Math.cos(spinAngle) * radius + randomX;
      posArray[i * 3 + 1] = randomY;
      posArray[i * 3 + 2] = Math.sin(spinAngle) * radius + randomZ;

      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      colorArray[i * 3] = randomColor.r;
      colorArray[i * 3 + 1] = randomColor.g;
      colorArray[i * 3 + 2] = randomColor.b;
    }

    galaxyParticlesGeo.setAttribute("position", new THREE.BufferAttribute(posArray, 3));
    galaxyParticlesGeo.setAttribute("color", new THREE.BufferAttribute(colorArray, 3));

    const galaxyParticlesMat = new THREE.PointsMaterial({
      size: 0.055,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
    });

    const galaxyMesh = new THREE.Points(galaxyParticlesGeo, galaxyParticlesMat);
    galaxyMesh.rotation.x = Math.PI / 6; // Tilt galaxy slightly
    scene.add(galaxyMesh);

    // 5. 3D Major Tech Planets & Orbit Rings (Scaled down radii: 1.5, 2.0, 2.5, 3.0)
    const planetsGroup = new THREE.Group();
    planetsGroup.rotation.x = Math.PI / 6;

    // Helper function to create orbit ring line
    const createOrbitRing = (radius, colorHex) => {
      const ringGeo = new THREE.TorusGeometry(radius, 0.015, 16, 100);
      const ringMat = new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.3 });
      const ringMesh = new THREE.Mesh(ringGeo, ringMat);
      ringMesh.rotation.x = Math.PI / 2;
      return ringMesh;
    };

    // Planet 1: React Atom Planet (Cyan - Radius 1.5)
    planetsGroup.add(createOrbitRing(1.5, 0x38bdf8));
    const reactPlanetGroup = new THREE.Group();
    const reactPlanet = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 16, 16),
      new THREE.MeshStandardMaterial({ color: 0x38bdf8, roughness: 0.2, metalness: 0.7 })
    );
    reactPlanetGroup.add(reactPlanet);
    const pRing = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.018, 16, 32), new THREE.MeshBasicMaterial({ color: 0x7dd3fc, wireframe: true }));
    reactPlanetGroup.add(pRing);
    planetsGroup.add(reactPlanetGroup);

    // Planet 2: JavaScript Planet (Gold - Radius 2.0)
    planetsGroup.add(createOrbitRing(2.0, 0xf59e0b));
    const jsPlanet = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.2, 0),
      new THREE.MeshStandardMaterial({ color: 0xf59e0b, roughness: 0.2, metalness: 0.8 })
    );
    planetsGroup.add(jsPlanet);

    // Planet 3: Fullstack / Node Planet (Emerald - Radius 2.5)
    planetsGroup.add(createOrbitRing(2.5, 0x10b981));
    const nodePlanet = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.22, 0),
      new THREE.MeshStandardMaterial({ color: 0x10b981, roughness: 0.2, metalness: 0.7 })
    );
    planetsGroup.add(nodePlanet);

    // Planet 4: AI & Future Planet (Magenta - Radius 3.0)
    planetsGroup.add(createOrbitRing(3.0, 0xec4899));
    const aiPlanet = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.24, 0),
      new THREE.MeshStandardMaterial({ color: 0xec4899, roughness: 0.2, metalness: 0.7 })
    );
    planetsGroup.add(aiPlanet);

    // 6. Vành đai các tiểu hành tinh 3D (35+ Micro Asteroids Belt - Scaled radius 1.8)
    const asteroidBeltGroup = new THREE.Group();
    const asteroidMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, roughness: 0.4, metalness: 0.6 });
    const asteroidGeos = [
      new THREE.DodecahedronGeometry(0.06, 0),
      new THREE.IcosahedronGeometry(0.05, 0),
      new THREE.TetrahedronGeometry(0.07, 0),
    ];

    for (let a = 0; a < 35; a++) {
      const geo = asteroidGeos[a % asteroidGeos.length];
      const asteroid = new THREE.Mesh(geo, asteroidMat);
      const angle = (a / 35) * Math.PI * 2;
      const radius = 1.8 + (Math.random() - 0.5) * 0.25;
      asteroid.position.set(Math.cos(angle) * radius, (Math.random() - 0.5) * 0.2, Math.sin(angle) * radius);
      asteroidBeltGroup.add(asteroid);
    }
    planetsGroup.add(asteroidBeltGroup);

    scene.add(planetsGroup);

    // Mouse Interaction
    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouseX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouseY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };

    window.addEventListener("mousemove", handleMouseMove);

    // 60 FPS Milky Way Galaxy Render Loop
    let animationFrameId;
    let clock = new THREE.Clock();

    const animate = () => {
      const time = clock.getElapsedTime();

      // 3D Earth Surface & Clouds Rotation
      earthMesh.rotation.y = time * 0.15;
      cloudMesh.rotation.y = time * 0.24;
      atmosphereMesh.rotation.y = -time * 0.1;
      shield.rotation.x = time * 0.5;
      shield.rotation.z = time * 0.4;
      coreLight.intensity = 4.0 + Math.sin(time * 2) * 1.0;

      // Milky Way Galaxy Spiral Arms Slow Rotation
      galaxyMesh.rotation.y = time * 0.12;

      // Orbit 1: React Planet (speed 1.1 - radius 1.5)
      const r1 = 1.5;
      const a1 = time * 1.1;
      reactPlanetGroup.position.set(Math.cos(a1) * r1, 0, Math.sin(a1) * r1);
      reactPlanet.rotation.y = time * 2;
      pRing.rotation.z = time * 2.5;

      // Orbit 2: JS Planet (speed 0.8 - radius 2.0)
      const r2 = 2.0;
      const a2 = time * 0.8 + 1.5;
      jsPlanet.position.set(Math.cos(a2) * r2, 0, Math.sin(a2) * r2);
      jsPlanet.rotation.x = time * 1.8;

      // Orbit 3: Node Planet (speed 0.6 - radius 2.5)
      const r3 = 2.5;
      const a3 = time * 0.6 + 3.0;
      nodePlanet.position.set(Math.cos(a3) * r3, 0, Math.sin(a3) * r3);
      nodePlanet.rotation.y = time * 1.5;

      // Orbit 4: AI Planet (speed 0.45 - radius 3.0)
      const r4 = 3.0;
      const a4 = time * 0.45 + 4.5;
      aiPlanet.position.set(Math.cos(a4) * r4, 0, Math.sin(a4) * r4);
      aiPlanet.rotation.z = time * 1.2;

      // Asteroid Belt Slow Rotation
      asteroidBeltGroup.rotation.y = time * 0.25;

      // Smooth Camera 3D Orbit Following Mouse
      camera.position.x += (mouseX * 1.2 - camera.position.x) * 0.06;
      camera.position.y += (3.2 + mouseY * 0.7 - camera.position.y) * 0.06;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Resize Handler
    const handleResize = () => {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return (
    <div className="artistic-hero-container home-hero-3d-container" style={{ position: "relative", width: "100%", height: "620px", overflow: "visible" }}>
      {/* 3D WebGL Milky Way Galaxy Canvas */}
      <div ref={mountRef} className="three-webgl-canvas" style={{ width: "100%", height: "100%" }} />

      {/* Floating 3D Animated Badges around Canvas */}
      <div className="hero-animated-objects">
        <div className="hero-obj obj-code-bracket" title="Code Symbol">
          <div className="obj-clay-orb orb-purple">
            <FaCode className="code-symbol-icon" />
          </div>
        </div>

        <div className="hero-obj obj-react-atom" title="React Atom">
          <div className="obj-clay-orb orb-blue">
            <FaAtom className="atom-icon" />
          </div>
        </div>

        <div className="hero-obj obj-gear" title="3D Gear">
          <div className="obj-clay-orb orb-pink">
            <FaCog className="gear-icon" />
          </div>
        </div>

        <div className="hero-obj obj-coffee" title="Coffee Steam">
          <div className="obj-clay-orb orb-amber">
            <FaCoffee className="coffee-icon" />
          </div>
        </div>

        <div className="hero-obj obj-clay-bubble bubble-1"></div>
        <div className="hero-obj obj-clay-bubble bubble-2"></div>
        <div className="hero-obj obj-clay-bubble bubble-3"></div>
      </div>

      {/* Cyber Ambient Glowing Backdrop Aura */}
      <div className="artistic-glow-aura"></div>
    </div>
  );
}

export default ThreeHomeScene;
